import { useModal } from "@pulse/hooks/contextHooks";
import { SuccessModal } from "./components/SuccessModal";
import { useTranslation } from "react-i18next";
import { bulkParticipants, type BulkParticipant } from "@pulse/api/participantApi";
import { useMutation } from "@tanstack/react-query";
import type { Survey } from "@pulse/api/surveyApi";
import { sendInvitation } from "@pulse/api/surveyEmailAPI";
import { queryClient } from "@pulse/queryClient";
import {
    getSurveysOptions,
    getSurveyOptions,
    ParticipantCountQueryOptions,
    ParticipantsForSurveyQueryOptions,
} from "@pulse/api/queryApi";
import { useAddParticipantForm } from "./form-hooks";
import { ParticipantSearchProvider } from "./components/ParticipantSearchProvider";
import { MAX_PARTICIPANTS } from "./constants";

export function ParticipantsForm({ survey }: { survey: Survey }) {
    const { openModal, closeModal, disableCloseOnClickedOutside } = useModal();
    const { t } = useTranslation();

    const { mutateAsync: sendEmails } = useMutation({
        mutationFn: sendInvitation,
        onSuccess: (response) => {
            queryClient.invalidateQueries(getSurveyOptions({ surveyId: survey.id }));
            queryClient.refetchQueries(
                getSurveysOptions({ pulseInstanceId: survey.pulseInstanceId }),
            );
            queryClient.invalidateQueries(ParticipantCountQueryOptions({ surveyId: survey.id }));
            queryClient.invalidateQueries(
                ParticipantsForSurveyQueryOptions({ surveyId: survey.id }),
            );
            // Close the participants modal and immediately open the success modal
            closeModal();
            openModal({
                title: t("survey.start"),
                children: <SuccessModal failedEmails={response.failed} survey={survey} />,
                disableXButton: true,
            });
        },
    });

    const { mutateAsync: submitParticipants } = useMutation({
        mutationFn: bulkParticipants,
        onSuccess: () => {
            disableCloseOnClickedOutside();
        },
        onError: (error) => {
            console.error("Participants submission error:", error);
        },
    });

    const form = useAddParticipantForm({
        defaultValues: {
            participants: [] as BulkParticipant[],
        },
        onSubmit: async (values) => {
            await submitParticipants({
                surveyId: survey.id,
                data: values.value.participants.map((p) =>
                    p.isExternal
                        ? {
                              email: p.email,
                          }
                        : {
                              userId: p.userId,
                          },
                ),
            });
            await sendEmails({ surveyId: survey.id });
            return { status: "success" };
        },
        listeners: {
            onChange: ({ formApi }) => {
                if (formApi.state.isDirty) {
                    disableCloseOnClickedOutside();
                }
            },
        },
    });

    return (
        <form
            className="flex flex-col min-h-0 w-120 max-w-full min-w-0 flex-1"
            onSubmit={(e) => {
                e.preventDefault();
                e.stopPropagation();
                form.handleSubmit();
            }}
        >
            <ParticipantSearchProvider>
                <div className=" min-h-0 flex-1 overflow-y-auto">
                    <form.AppField
                        name="participants"
                        mode="array"
                        validators={{
                            onChange: ({ value }) => {
                                if (value.length > MAX_PARTICIPANTS) {
                                    return {
                                        type: "maxLength",
                                        message: t("addParticipants.maxParticipants", {
                                            count: MAX_PARTICIPANTS,
                                        }),
                                    };
                                }
                                if (value.length === 0) {
                                    return {
                                        type: "required",
                                        message: t("addParticipants.required"),
                                    };
                                }
                                return undefined;
                            },
                        }}
                    >
                        {(field) => <field.ParticipantField />}
                    </form.AppField>
                </div>
                <form.AppForm>
                    <form.FormSubmitButton />
                </form.AppForm>
            </ParticipantSearchProvider>
        </form>
    );
}
